import React, { useState, useEffect, useRef } from 'react';
import { 
  BellAlertIcon, 
  XMarkIcon, 
  SpeakerWaveIcon, 
  SpeakerXMarkIcon,
  PlayIcon,
  ClockIcon
} from '@heroicons/react/24/outline';
import { ClassScheduleEntry } from '../../types';

// Play a pleasant 2-tone melodic notification chime using Web Audio API
export const playNotificationChime = () => {
  try {
    const AudioContextClass = window.AudioContext || (window as any).webkitAudioContext;
    if (!AudioContextClass) return;
    const ctx = new AudioContextClass();
    const now = ctx.currentTime;

    // Tone 1 (D5 - 587.33 Hz)
    const osc1 = ctx.createOscillator();
    const gain1 = ctx.createGain();
    osc1.type = 'sine';
    osc1.frequency.setValueAtTime(587.33, now);
    gain1.gain.setValueAtTime(0.15, now);
    gain1.gain.exponentialRampToValueAtTime(0.001, now + 0.35);
    osc1.connect(gain1);
    gain1.connect(ctx.destination);
    osc1.start(now);
    osc1.stop(now + 0.35);

    // Tone 2 (A5 - 880 Hz)
    const osc2 = ctx.createOscillator();
    const gain2 = ctx.createGain();
    osc2.type = 'sine';
    osc2.frequency.setValueAtTime(880, now + 0.15);
    gain2.gain.setValueAtTime(0.2, now + 0.15);
    gain2.gain.exponentialRampToValueAtTime(0.001, now + 0.6);
    osc2.connect(gain2);
    gain2.connect(ctx.destination);
    osc2.start(now + 0.15);
    osc2.stop(now + 0.6);
  } catch {
    // Audio context may be restricted by autoplay policy until user gesture
  }
};

interface ClassUpcomingNotificationBannerProps {
  schedules: ClassScheduleEntry[];
  role: 'candidate' | 'instructor';
  instructorId?: string;
  onGoLiveClick?: () => void;
  isCurrentlyLive?: boolean;
}

const ClassUpcomingNotificationBanner: React.FC<ClassUpcomingNotificationBannerProps> = ({
  schedules,
  role,
  instructorId,
  onGoLiveClick,
  isCurrentlyLive = false
}) => {
  const [activeAlert, setActiveAlert] = useState<{
    schedule: ClassScheduleEntry;
    diffMs: number;
    minutesLeft: number;
    secondsLeft: number;
  } | null>(null);

  const [dismissedScheduleId, setDismissedScheduleId] = useState<string | null>(null);
  const [soundMuted, setSoundMuted] = useState(false);
  const [browserNotificationAllowed, setBrowserNotificationAllowed] = useState(
    typeof window !== 'undefined' && 'Notification' in window && Notification.permission === 'granted'
  );

  const chimePlayedForScheduleRef = useRef<Record<string, boolean>>({});
  const browserNotifSentRef = useRef<Record<string, boolean>>({});

  // Request browser notification permission
  const handleRequestPermission = async () => {
    if (typeof window === 'undefined' || !('Notification' in window)) {
      alert('Notifications are not supported in this browser.');
      return;
    }

    if (typeof window.isSecureContext === 'boolean' && !window.isSecureContext) {
      if (window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1') {
        alert('Google Chrome requires a secure connection (HTTPS) to allow notifications.');
        return;
      }
    }

    try {
      const res = await Notification.requestPermission();
      if (res === 'granted') {
        setBrowserNotificationAllowed(true);
        playNotificationChime();
        try {
          new Notification('🔔 Notifications Enabled!', {
            body: 'You will receive reminders when class sessions begin.',
            icon: '/logo192.png'
          });
        } catch {}
      } else if (res === 'denied') {
        alert(
          'Notifications are currently blocked in Chrome for this site.\n\nTo enable alerts:\n1. Click the tune/padlock icon (🔒) left of the URL in Chrome\'s address bar.\n2. Set Notifications to "Allow".\n3. Reload the page.'
        );
      }
    } catch (e) {
      console.error('Failed to request notification permission:', e);
    }
  };

  useEffect(() => {
    // If currently live broadcast is playing, no need to show upcoming 20-min countdown
    if (isCurrentlyLive) {
      setActiveAlert(null);
      return;
    }

    const checkSchedules = () => {
      const now = Date.now();

      // Filter relevant schedules
      const candidates = schedules.filter((entry) => {
        if (role === 'instructor' && instructorId) {
          // If instructor, match instructorId (or unassigned for admin)
          if (entry.instructorId && entry.instructorId !== instructorId) {
            return false;
          }
        }
        return true;
      });

      let nearestAlert: {
        schedule: ClassScheduleEntry;
        diffMs: number;
        minutesLeft: number;
        secondsLeft: number;
      } | null = null;

      for (const entry of candidates) {
        const schedDate = entry.scheduledAt?.toDate 
          ? entry.scheduledAt.toDate() 
          : new Date(entry.scheduledAt);
        const schedTime = schedDate.getTime();
        const diffMs = schedTime - now;

        // 20 Minutes Window: between 0 and 20 minutes (20 * 60 * 1000 = 1,200,000 ms)
        if (diffMs > 0 && diffMs <= 20 * 60 * 1000) {
          const minutesLeft = Math.floor(diffMs / 60000);
          const secondsLeft = Math.floor((diffMs % 60000) / 1000);

          if (!nearestAlert || diffMs < nearestAlert.diffMs) {
            nearestAlert = {
              schedule: entry,
              diffMs,
              minutesLeft,
              secondsLeft
            };
          }
        }
      }

      setActiveAlert(nearestAlert);

      // Trigger alerts (sound + browser notification) when entering 20-min window
      if (nearestAlert && dismissedScheduleId !== nearestAlert.schedule.id) {
        const id = nearestAlert.schedule.id;

        // Play chime once per schedule entry
        if (!chimePlayedForScheduleRef.current[id] && !soundMuted) {
          chimePlayedForScheduleRef.current[id] = true;
          playNotificationChime();
        }

        // Fire OS desktop notification if allowed
        if (
          !browserNotifSentRef.current[id] && 
          typeof window !== 'undefined' && 
          'Notification' in window && 
          Notification.permission === 'granted'
        ) {
          browserNotifSentRef.current[id] = true;
          try {
            const notifTitle = role === 'instructor'
              ? `🔔 Instructor Alert: Class in ${nearestAlert.minutesLeft} mins!`
              : `🔔 Class Alert: Starting in ${nearestAlert.minutesLeft} mins!`;
            
            const notifBody = role === 'instructor'
              ? `"${nearestAlert.schedule.title}" begins in ${nearestAlert.minutesLeft} minutes. Please prepare your live stream and go live!`
              : `"${nearestAlert.schedule.title}"${nearestAlert.schedule.instructorName ? ` with ${nearestAlert.schedule.instructorName}` : ''} begins in ${nearestAlert.minutesLeft} minutes.`;

            new Notification(notifTitle, {
              body: notifBody,
              icon: '/logo192.png'
            });
          } catch {
            // Ignore notification construction errors
          }
        }
      }
    };

    checkSchedules();
    const interval = setInterval(checkSchedules, 1000);
    return () => clearInterval(interval);
  }, [schedules, role, instructorId, isCurrentlyLive, soundMuted, dismissedScheduleId]);

  if (!activeAlert || dismissedScheduleId === activeAlert.schedule.id) {
    return null;
  }

  const { schedule, minutesLeft, secondsLeft } = activeAlert;
  const formattedCountdown = `${minutesLeft.toString().padStart(2, '0')}:${secondsLeft.toString().padStart(2, '0')}`;

  return (
    <aside aria-label="Upcoming class countdown alert" className="relative z-50 mb-6 animate-fadeIn">
      <div className={`rounded-2xl p-4 sm:p-5 border shadow-2xl backdrop-blur-xl transition-all duration-300 ${
        role === 'instructor'
          ? 'bg-gradient-to-r from-indigo-900/90 via-purple-900/80 to-slate-900/90 border-indigo-400/50 text-white shadow-indigo-500/20'
          : 'bg-gradient-to-r from-amber-900/90 via-orange-950/80 to-primary-950/90 border-amber-400/50 text-white shadow-amber-500/20'
      }`}>
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          {/* Left: Icon, Countdown & Details */}
          <div className="flex items-start sm:items-center space-x-3 sm:space-x-4 min-w-0">
            {/* Animated Ring Indicator */}
            <div className="relative flex-shrink-0 mt-0.5 sm:mt-0">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75" />
              <div className="relative w-11 h-11 sm:w-12 sm:h-12 rounded-2xl bg-amber-500/30 border border-amber-400/60 flex items-center justify-center text-amber-300 shadow-md">
                <BellAlertIcon className="w-6 h-6 animate-bounce" />
              </div>
            </div>

            <div className="space-y-1 min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <span className="px-2.5 py-0.5 rounded-full text-xs font-black uppercase tracking-wider bg-amber-500 text-slate-950 shadow-sm flex items-center space-x-1">
                  <ClockIcon className="w-3.5 h-3.5" />
                  <span>STARTS IN {formattedCountdown}</span>
                </span>

                <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-white/15 border border-white/20 text-white">
                  20-Min Notice
                </span>

                {schedule.batchLabel && (
                  <span className="text-xs px-2 py-0.5 rounded-full bg-indigo-500/30 text-indigo-200 border border-indigo-400/30 font-medium">
                    {schedule.batchLabel}
                  </span>
                )}
              </div>

              <h4 className="text-base sm:text-lg font-bold text-white tracking-tight truncate">
                {schedule.title}
              </h4>

              <p className="text-xs text-amber-100/90">
                {role === 'instructor' ? (
                  <span>
                    Your assigned class session begins in <strong className="text-white font-mono">{formattedCountdown}</strong>. Live broadcast controls are ready.
                  </span>
                ) : (
                  <span>
                    {schedule.instructorName && (
                      <span>With Instructor <strong className="text-white">{schedule.instructorName}</strong> &bull; </span>
                    )}
                    Please stay on this page. When broadcast begins, lecture video will play automatically.
                  </span>
                )}
              </p>
            </div>
          </div>

          {/* Right: Actions */}
          <div className="flex items-center space-x-2 w-full md:w-auto justify-end pt-2 md:pt-0 border-t md:border-t-0 border-white/15">
            {/* Permission Prompt if not granted */}
            {!browserNotificationAllowed && typeof window !== 'undefined' && 'Notification' in window && (
              <button
                type="button"
                onClick={handleRequestPermission}
                className="text-xs px-2.5 py-1.5 rounded-lg bg-white/10 hover:bg-white/20 text-amber-200 hover:text-white border border-white/20 transition-all font-medium whitespace-nowrap"
                title="Enable desktop notifications"
              >
                🔔 Allow Alerts
              </button>
            )}

            {/* Mute/Unmute Chime */}
            <button
              type="button"
              onClick={() => {
                const nextMuted = !soundMuted;
                setSoundMuted(nextMuted);
                if (!nextMuted) playNotificationChime();
              }}
              className="p-2 rounded-lg bg-white/10 hover:bg-white/20 text-white border border-white/20 transition-all"
              title={soundMuted ? 'Unmute notification chime' : 'Mute notification chime'}
            >
              {soundMuted ? (
                <SpeakerXMarkIcon className="w-4 h-4 text-slate-400" />
              ) : (
                <SpeakerWaveIcon className="w-4 h-4 text-amber-300" />
              )}
            </button>

            {/* Go Live Action for Instructor */}
            {role === 'instructor' && onGoLiveClick && (
              <button
                type="button"
                onClick={onGoLiveClick}
                className="px-4 py-2 bg-gradient-to-r from-emerald-500 to-green-600 hover:from-emerald-400 hover:to-green-500 text-white font-bold text-xs rounded-xl shadow-lg shadow-emerald-500/30 flex items-center space-x-1.5 transition-all"
              >
                <PlayIcon className="w-4 h-4" />
                <span>Go Live Now</span>
              </button>
            )}

            {/* Dismiss / Close Button */}
            <button
              type="button"
              onClick={() => setDismissedScheduleId(schedule.id)}
              className="p-2 rounded-lg bg-white/10 hover:bg-white/20 text-white/80 hover:text-white border border-white/20 transition-all"
              title="Dismiss for this session"
            >
              <XMarkIcon className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </aside>
  );
};

export default ClassUpcomingNotificationBanner;
